import Router from 'next/router';
import { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { getVariant_AppendIncompleteSearchResults } from '@bridebook/web/lib/ab-tests/tests/global/LIVE-27907_AppendIncompleteSearchResults';
import { selectMarketWithFallback } from '@bridebook/web/lib/i18n/selectors';
import type { SearchResponseFields } from '@bridebook/web/lib/search/types';
import { getIsCountrySearch } from '@bridebook/web/lib/search/utils/get-is-country-search';
import type { IApplicationState } from '@bridebook/web/lib/types';
import type { PlannerBody } from './schemas';
import { SupplierTypeEnum } from './supplier-types';
import {
  SEARCH_AREA_MAX_CHARS,
  SEARCH_INTERACTIONS_MAX,
  type SearchContext,
} from './tools/search-suppliers/context';

/**
 * The couple's account and current search, read from the store when a message is sent.
 * Plain reads, not hooks: the panel mounts above every page, and a subscription would
 * re-render it on every route change for values only needed at send time.
 */
export type PlannerRequestContext = Pick<PlannerBody, 'account' | 'search'>;

const onSearchPage = (): boolean => Router.pathname.startsWith('/[market]/search');

// The personalisation the search engine returned for the results on screen. The lists are
// cut to the request cap, so a long history stays a valid request.
const personalisationOf = (fields: SearchResponseFields): SearchContext['personalisation'] => {
  const { interactions, interactionActions, weddingFeatures, weddingBudget } = fields;
  const present = Object.fromEntries(
    Object.entries({
      interactions: interactions?.slice(0, SEARCH_INTERACTIONS_MAX),
      interactionActions: interactionActions?.slice(0, SEARCH_INTERACTIONS_MAX),
      weddingFeatures,
      weddingBudget,
    }).filter(([, value]) => value != null),
  );
  return Object.keys(present).length > 0 ? present : undefined;
};

/**
 * The couple's supplier search: whether they are on the results page, the place and
 * category, the results personalisation and any A/B test settings. The place is their last
 * pick, or their wedding location until they pick one.
 */
export const plannerSearchContext = (state: IApplicationState): SearchContext => {
  const { searchLocation, results, request } = state.search;
  const onPage = onSearchPage();
  const selected = searchLocation.status === 'initialized' ? searchLocation.selected : null;
  // A country-wide search names the country. Sent as a place, it would turn into a town
  // lookup, so it is sent as no place.
  const area =
    selected?.area && !getIsCountrySearch(selected.area)
      ? selected.area.slice(0, SEARCH_AREA_MAX_CHARS)
      : null;
  // A search category the planner does not support is sent as none.
  const category = SupplierTypeEnum.safeParse(request.slug);

  // Only on the search page, so the planner's results rank like the list on screen.
  const fields = onPage && results.status === 'loaded' ? results.fields : null;

  // The test applies to GB venues only. The venue check happens where the search is built,
  // because only there is the category known.
  const variant = getVariant_AppendIncompleteSearchResults(state);
  const country = state.weddings.profile.l10n?.country;

  return {
    onPage,
    area,
    type: category.success ? category.data : null,
    personalisation: fields ? personalisationOf(fields) : undefined,
    tuning:
      variant === '1' && country === CountryCodes.GB ? { appendIncompleteSearch: true } : undefined,
  };
};

export const plannerRequestContext = (state: IApplicationState): PlannerRequestContext => ({
  account: { locale: selectMarketWithFallback(state).locale },
  search: plannerSearchContext(state),
});
